// src/components/tiles/TilesShowcase.jsx
import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Chip,
  Stack,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import { slugify } from '../../utils/slug';
import SectionHeader from '../SectionHeader';
import { getSectionKind } from '../../utils/sectionKind';
import TableReveal from '../TableReveal';
import JobTile from './JobTile';
import TriggerTile from './TriggerTile';
import SolutionTile from './SolutionTile';
import ImpressionTile from './ImpressionTile';
import WorkflowStepTile from './WorkflowStepTile';
import InsightTile from './InsightTile';
import SituationTile from './SituationTile';
import SituationsTable from '../detail/tables/SituationsTable';
import MeasureTile from './MeasureTile';
import MeasuresTable from '../detail/tables/MeasuresTable';
import JobStepTile from './JobStepTile';
import JobSatisfactionSummaryTile from './JobSatisfactionSummaryTile';
import ProductForJobTile from './ProductForJobTile';
import ProductsTable from '../detail/tables/ProductsTable';
import NextJobTile from './NextJobTile';
import JobMapBoard from './JobMapBoard';
import TotalInterviewsPill from './TotalInterviewsPill';
import DesiredOutcomesTable from '../detail/tables/DesiredOutcomesTable';
import UnifiedInsightsTable from '../detail/tables/InsightsTable';
import JobInterviewsTable from '../detail/tables/JobInterviewsTable';
import JobsTable from '../detail/tables/JobsTable';
import ProductInterviewsTable from '../detail/tables/ProductInterviewsTable';
import TriggersTable from '../detail/tables/TriggersTable';
import PreviousSolutionsTable from '../detail/tables/PreviousSolutionsTable';
import ConsideredSolutionsTable from '../detail/tables/ConsideredSolutionsTable';
import PositivesOfSolutionTable from '../detail/tables/PositivesOfSolutionTable';
import DrawbacksOfSolutionTable from '../detail/tables/DrawbacksOfSolutionTable';
import SituationsForProductTables from '../detail/tables/SituationsForProductTables';
import { computeWinScore, computeGapScore } from '../../utils/scoring';
import { useDashboardData } from '../../context/DashboardDataContext';

const registry = {
  job: JobTile,
  trigger: TriggerTile,
  solution: SolutionTile,
  impression: ImpressionTile,
  workflowStep: WorkflowStepTile,
  insight: InsightTile,
  situation: SituationTile,
  measure: MeasureTile,
  jobStep: JobStepTile,
  jobSatisfactionSummary: JobSatisfactionSummaryTile,
  productForJob: ProductForJobTile,
  nextJob: NextJobTile,
};

// Fixed order for Job Map stages
const STAGE_ORDER = ['define','locate','prepare','confirm','execute','monitor','modify','conclude'];
const stageRank = (s='') => {
  const i = STAGE_ORDER.indexOf(String(s).toLowerCase());
  return i === -1 ? 999 : i;
};

// Build rows for JobsTable from customer stories + selected product
function buildJobsRowsForProduct(stories = [], productName) {
    const target = slugify(productName || '');
    const matches = (stories || []).filter(
      (s) => s?.solution_choice_category && slugify(s.solution_choice_category) === target
    );
    const totalInterviews = matches.length;
    if (!totalInterviews) return { rows: [], sectionTotal: 0 };
  
    const jobMap = new Map(); // jobSlug -> { name, count, sumSat, nSat, instances: [] }
  
    matches.forEach((s) => {
      const jobs = Array.isArray(s?.jobs_to_be_done) ? s.jobs_to_be_done : [];
      const seenThisInterview = new Set();
      const storySat = Number(s?.satisfaction_level);
      const hasStorySat = Number.isFinite(storySat);
  
      jobs.forEach((j) => {
        const name = j?.job_to_be_done_category;
        if (!name) return;
        const key = slugify(name);
        if (seenThisInterview.has(key)) return;
        seenThisInterview.add(key);
  
        if (!jobMap.has(key)) {
          jobMap.set(key, { name, count: 0, sumSat: 0, nSat: 0, instances: [] });
        }
        const g = jobMap.get(key);
        g.count += 1;
        if (hasStorySat) {
          g.sumSat += storySat;
          g.nSat += 1;
        }
  
        g.instances.push({
          name: s.customer_name || 'Interview',
          date: s.conversation_date || '',
          segmentation: s.segmentation_data || {},
          chosenSolution: s.solution_choice_category || '',
          importance: Number(j?.importance ?? ''),
          satisfaction: Number(j?.satisfaction ?? ''),
          summary: j?.satisfaction_justification || '',
          href: `/conversations?search=${encodeURIComponent(s.customer_name || '')}`,
        });
      });
    });
  
    const rows = [...jobMap.values()].map((g) => {
      const avgProductSat = g.nSat ? g.sumSat / g.nSat : null;
      const pct = Math.round((g.count / totalInterviews) * 100);
      return {
        name: g.name,
        count: g.count,
        pct,
        avgProductSat,
        href: `/job/${slugify(g.name)}`,
        instances: g.instances.sort((a, b) => (Date.parse(b.date||'')||0) - (Date.parse(a.date||'')||0)),
      };
    });
    rows.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return { rows, sectionTotal: totalInterviews };
  }

export default function TilesShowcase({ mode, selected }) {
  const { ready, error, getProductSections, getJobSections, customerStories } = useDashboardData();

  // For Product view tables (TriggersTable, JobsTable, etc.)
  const productStories = React.useMemo(() => {
    if (mode !== 'product' || !selected) return [];
    const target = slugify(selected || '');
    return (customerStories || []).filter(
      (s) => slugify(s?.solution_choice_category || '') === target
    );
  }, [mode, selected, customerStories]);

  const jobsForProduct = React.useMemo(() => {
      if (mode !== 'product' || !selected) return { rows: [], sectionTotal: 0 };
      return buildJobsRowsForProduct(customerStories || [], selected);
    }, [mode, selected, customerStories]);
  
  // Build sections from selectors when ready
  const sections = React.useMemo(() => {
    if (!ready || !selected) return [];
    if (mode === 'product') {
      const { sections } = getProductSections(selected);
      return sections || [];
    }
    // Job view
    const { sections } = getJobSections(selected);
    return sections || [];
  }, [ready, selected, mode, getProductSections, getJobSections]);

  // Hide the Job-view Triggers section (not needed there, remove it from data)
  const sectionsFiltered = React.useMemo(() => {
    if (!sections?.length) return [];
    if (mode !== 'job') return sections;

    // robust match: any job section whose title includes "triggers"
    const isJobTriggers = (s) => String(s.title || '').toLowerCase().includes('triggers');
    return sections.filter((s) => !isJobTriggers(s));
  }, [sections, mode]);

  // Local state: which “Top Job” row is selected per section index (only for Signature UX section)
  const [sigUxRowIndexBySection, setSigUxRowIndexBySection] = React.useState({});

  // Simple loading/empty states
  if (!ready) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading dashboard sections…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="error">
        Could not load dashboard sections. Check the console for details.
      </Typography>
    );
  }
  if (!sections.length) return null;

  const renderTilesGrid = (tiles, sectionTotal) => (
    <Grid container spacing={2}>
      {(tiles || []).map((t, i) => {
        const Comp = registry[t.type] || (() => null);
        const gridProps = t.type === 'jobSatisfactionSummary' ? { xs: 12 } : { xs: 12, sm: 6, md: 4, lg: 3 };
        return (
          <Grid item key={i} {...gridProps}>
            <Comp {...t} sectionTotal={sectionTotal} />
          </Grid>
        );
      })}
    </Grid>
  );

  const introCopy = (title) => {
    const map = {
      // Product view
      "Where does this solution win vs struggle?": "The Jobs this product is strongest and weakest at, by Win Score and Gap Score.",
      "Why do customers switch to this solution? (Triggers)": "The most common reasons customers switched to this solution from their previous solution.",
      "What do they switch away from? (Previous solutions)": "The most common solutions used before switching to this solution.",
      "What else do they consider? (Considered solutions)": "The solutions they considered and top reasons they didn’t pick them.",
      "What are their overall impressions of this Product?": "Themes that shape perception; scores reflect importance × interviews.",
      "Signature UX patterns (Product workflow steps)": "Top jobs this product supports; four representative steps shown.",
      "What are their likes and dislikes in using this Product?": "What customers praise or wish were better; scores reflect sentiment × interviews.",
      "Situations where customers continue/stop using the Product": "Where the product still shows up—and where it no longer does.",

      // Job view
      "Overview": "How important this job is and how satisfied customers are, with top drivers and quotes.",
      "How do customers measure success?": "Measures that define success; desired outcomes are shown as chips.",
      "The Job Map": "Every step to complete the job; click any step for product workflows (placeholder).",
      "Importance × Satisfaction": "Which products win or lag when used for this job.",
      "Why do customers seek out better solutions to this Job? (Triggers)": "What pushes customers to seek better ways; situations chips summarize common contexts.",
      "What 'good' & 'bad' look like": "Top positive and negative insights when doing this job.",
      "Next Jobs": "Where customers go next after completing this job.",
    };
    return map[title] || null;
  };

  // DEV: quick section/tile counts to ensure selectors populate
  if (sections?.length) {
    console.log('[TilesShowcase] Sections summary:',
      sections.map(s => ({
        title: s.title,
        tiles: s.tiles ? s.tiles.length : (s.rows ? s.rows.reduce((n, r) => n + (r.tiles?.length || 0), 0) : (s.subsections ? s.subsections.reduce((n, sub) => n + (sub.tiles?.length || 0), 0) : 0))
      }))
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {sectionsFiltered.map((section, idx) => {
        const sectionTotal = section.section_total_interviews ?? 0;
        const kind = getSectionKind(section, mode);

        // If this is the Job view’s “Importance × Satisfaction” section, sort product tiles:
        let tiles = section.tiles ? [...section.tiles] : [];
        if (mode === 'job' && kind === 'job.importanceSatisfaction') {
          const wins = tiles
            .filter(t => t.type === 'productForJob' && t.variant === 'win')
            .sort((a, b) =>
              computeWinScore({ importance: b.importance, satisfaction: b.satisfaction, interviews: b.count }) -
              computeWinScore({ importance: a.importance, satisfaction: a.satisfaction, interviews: a.count })
            );
          const gaps = tiles
            .filter(t => t.type === 'productForJob' && t.variant === 'gap')
            .sort((a, b) =>
              computeGapScore({ importance: b.importance, satisfaction: b.satisfaction }) -
              computeGapScore({ importance: a.importance, satisfaction: a.satisfaction })
            );
          const others = tiles.filter(t => t.type !== 'productForJob' || (t.variant !== 'win' && t.variant !== 'gap'));
          tiles = [...others, ...wins, ...gaps];
        }

        const isSignatureUx =
          mode === 'product' && section.rows && section.title === 'Signature UX patterns (Product workflow steps)';

        return (
          <Box key={idx} sx={{ mb: 4 }}>
            <SectionHeader
              metaTitle={section.metaTitle ?? null}
              title={section.title}
              intro={introCopy(section.title)}
              updatedAt={section.updatedAt}
            />

            {/* Optional chips block (kept as chips, not tiles) */}
            {section.chips && section.chips.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {section.chipsLabel && <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{section.chipsLabel}</Typography>}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {section.chips.map((c, i) => (
                    <Chip
                      key={i}
                      size="small"
                      label={`${c.title}${c.count != null ? ` (${c.count}${c.percent != null ? ` · ${c.percent}%` : ''})` : ''}`}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Section layouts */}
            {section.type === 'jobMapBoard' ? (
              <JobMapBoard columns={section.columns || []} />
            ) : section.rows ? (
              isSignatureUx ? (
                // --- Signature UX patterns: show ONE row with a selector ---
                <Box sx={{ mb: 2 }}>
                  {/* Build selector options from rows */}
                  {(() => {
                    const options = (section.rows || []).map((row, i) => {
                      const count = row.rowInterviewCount || 0;
                      const pct = sectionTotal ? Math.round((count / sectionTotal) * 100) : 0;
                      // Use the provided rowLabel text if present; otherwise build from title
                      const label = `${row.rowLabel || `Top Job #${i + 1}`} (${count} interviews – ${pct}%)`;
                      return { i, label };
                    });

                    const selectedIndex =
                      sigUxRowIndexBySection[idx] != null ? sigUxRowIndexBySection[idx] : 0;

                    const handleChange = (_, val) => {
                      const nextIndex = (val && typeof val.i === 'number') ? val.i : 0;
                      setSigUxRowIndexBySection(prev => ({ ...prev, [idx]: nextIndex }));
                    };

                    const chosenRow = section.rows[selectedIndex] || section.rows[0] || { tiles: [] };

                    // Order the chosen row’s tiles by stage precedence, then by satisfaction desc
                    const orderedTiles = [...(chosenRow.tiles || [])]
                      .sort((a, b) => {
                        const r = stageRank(a.stage) - stageRank(b.stage);
                        if (r !== 0) return r;
                        const sa = Number.isFinite(a.satisfaction) ? a.satisfaction : -999;
                        const sb = Number.isFinite(b.satisfaction) ? b.satisfaction : -999;
                        return sb - sa;
                      });

                    return (
                      <>
                        {/* Selector replaces the “Top Job …” header/pill row */}
                        <Box sx={{ maxWidth: 520, mb: 2 }}>
                          <Autocomplete
                            options={options}
                            value={options.find(o => o.i === selectedIndex) || options[0] || null}
                            onChange={handleChange}
                            disableClearable
                            size="small"
                            getOptionLabel={(o) => o?.label || ''}
                            renderInput={(params) => (
                              <TextField {...params} label="Select a Top Job" />
                            )}
                          />
                        </Box>

                        
                        {/* Horizontal scroller of ALL tiles */}
                        <Box
                          sx={{
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            pb: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1
                          }}
                        >
                          <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 2, p: 2 }}>
                            {orderedTiles.map((t, i) => {
                              const Comp = registry[t.type] || (() => null);
                              return (
                                <Box key={i} sx={{ flex: '0 0 320px' }}>
                                  <Comp {...t} sectionTotal={sectionTotal} />
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </>
                    );
                  })()}
                </Box>
              ) : (
                <>
                  {/* Default rows rendering for other sections */}
                  {section.rows.map((row, ri) => (
                    <Box key={ri} sx={{ mb: 2 }}>
                      {row.rowLabel && (
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {row.rowLabel}
                          </Typography>
                          <TotalInterviewsPill count={row.rowInterviewCount || 0} />
                        </Stack>
                      )}
                      {renderTilesGrid(row.tiles, sectionTotal)}
                    </Box>
                  ))}

                  {/* PRODUCT view: Situations tables (Continue using AND No longer using) */}
                  {mode === 'product' && kind === 'product.situations' && (
                    <TableReveal label="Situations where customers continue/stop using the Product">
                      <SituationsForProductTables customerStories={customerStories || []} />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: Unified Insights under “What are their likes and dislikes in using this Product?” (rows layout) */}
                  {mode === 'product' && kind === 'product.likesDislikes' && (
                    <TableReveal label="Likes & dislikes (Insights)">
                      <UnifiedInsightsTable customerStories={customerStories || []} />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: Considered Solutions table */}
                  {mode === 'product' && kind === 'product.consideredSolutions' && (
                    <TableReveal label="Considered solutions">
                      <ConsideredSolutionsTable customerStories={productStories} />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: Previous Solutions table */}
                  {mode === 'product' && kind === 'product.previousSolutions' && (
                    <TableReveal label="Previous solutions">
                      <PreviousSolutionsTable customerStories={productStories} />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: Triggers table */}
                  {mode === 'product' && kind === 'product.triggers' && (
                    <TableReveal label="Triggers">
                      <TriggersTable
                        rowsSource={section.rows?.flatMap(r => r.tiles || []) || []}
                        customerStories={productStories}
                      />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: JobsTable under “Where does this solution win vs struggle?” */}
                  {mode === 'product' && kind === 'product.winVsStruggle' && (
                    <TableReveal label="Jobs this product is used for">
                      <JobsTable rows={jobsForProduct.rows} />
                    </TableReveal>
                  )}

                  {/* PRODUCT view: Positives/Drawbacks under “What are their overall impressions…” */}
                  {mode === 'product' && kind === 'product.impressions' && (
                    <TableReveal label="Overall impressions">
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Positives of this solution
                      </Typography>
                      <PositivesOfSolutionTable customerStories={customerStories || []} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                        Drawbacks of this solution
                      </Typography>
                      <DrawbacksOfSolutionTable customerStories={customerStories || []} />
                    </TableReveal>
                  )}

                  {/* JOB view: Unified Insights table under "What 'good' & 'bad' look like" */}
                  {mode === 'job' && kind === 'job.goodBad' && (
                    <TableReveal label="What ‘good’ & ‘bad’ look like (Insights)">
                      <UnifiedInsightsTable customerStories={customerStories || []} jobName={selected} />
                    </TableReveal>
                  )}
                </>
              )
            ) : section.subsections ? (
              <>
                {section.subsections.map((sub, si) => (
                  <Box key={si} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>{sub.title}</Typography>
                    {renderTilesGrid(sub.tiles, sectionTotal)}
                  </Box>
                ))}

                {/* PRODUCT view: Situations tables (subsections layout) */}
                {mode === 'product' && kind === 'product.situations' && (
                  <TableReveal label="Situations where customers continue/stop using the Product">
                    <SituationsForProductTables customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Unified Insights (subsections layout) */}
                {mode === 'product' && kind === 'product.likesDislikes' && (
                  <TableReveal label="Likes & dislikes (Insights)">
                    <UnifiedInsightsTable customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Positives/Drawbacks (subsections layout) */}
                {mode === 'product' && kind === 'product.impressions' && (
                  <TableReveal label="Overall impressions">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Positives of this solution
                    </Typography>
                    <PositivesOfSolutionTable customerStories={customerStories || []} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                      Drawbacks of this solution
                    </Typography>
                    <DrawbacksOfSolutionTable customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Considered Solutions (subsections layout) */}
                {mode === 'product' && kind === 'product.consideredSolutions' && (
                  <TableReveal label="Considered solutions">
                    <ConsideredSolutionsTable customerStories={productStories} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Previous Solutions (subsections layout) */}
                {mode === 'product' && kind === 'product.previousSolutions' && (
                  <TableReveal label="Previous solutions">
                    <PreviousSolutionsTable customerStories={productStories} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Triggers (subsections layout) */}
                {mode === 'product' && kind === 'product.triggers' && (
                  <TableReveal label="Triggers">
                    <TriggersTable
                      rowsSource={section.subsections?.flatMap(ss => ss.tiles || []) || []}
                      customerStories={productStories}
                    />
                  </TableReveal>
                )}
                
                {/* JOB view: Unified Insights (whole section) */}
                {mode === 'job' && kind === 'job.goodBad' && (
                  <TableReveal label="What ‘good’ & ‘bad’ look like (Insights)">
                    <UnifiedInsightsTable customerStories={customerStories || []} jobName={selected} />
                  </TableReveal>
                )}
              </>
            ) : (
              <>
                {renderTilesGrid(tiles, sectionTotal)}

                {/* PRODUCT view: Situations tables (plain tiles layout) */}
                {mode === 'product' && kind === 'product.situations' && (
                  <TableReveal label="Situations where customers continue/stop using the Product">
                    <SituationsForProductTables customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Unified Insights (plain tiles layout) */}
                {mode === 'product' && kind === 'product.likesDislikes' && (
                  <TableReveal label="Likes & dislikes (Insights)">
                    <UnifiedInsightsTable customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Positives/Drawbacks under “What are their overall impressions…” */}
                {mode === 'product' && kind === 'product.impressions' && (
                  <TableReveal label="Overall impressions">
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Positives of this solution
                    </Typography>
                    <PositivesOfSolutionTable productName={selected} customerStories={customerStories || []} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>
                      Drawbacks of this solution
                    </Typography>
                    <DrawbacksOfSolutionTable productName={selected} customerStories={customerStories || []} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Considered Solutions (plain tiles layout) */}
                {mode === 'product' && kind === 'product.consideredSolutions' && (
                  <TableReveal label="Considered solutions">
                    <ConsideredSolutionsTable customerStories={productStories} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Previous Solutions (plain tiles layout) */}
                {mode === 'product' && kind === 'product.previousSolutions' && (
                  <TableReveal label="Previous solutions">
                    <PreviousSolutionsTable customerStories={productStories} />
                  </TableReveal>
                )}

                {/* PRODUCT view: Triggers (plain tiles layout) */}
                {mode === 'product' && kind === 'product.triggers' && (
                  <TableReveal label="Triggers">
                    <TriggersTable rowsSource={tiles} customerStories={productStories} />
                  </TableReveal>
                )}

                {/* JOB view: Situations table */}
                {mode === 'job' && kind === 'job.situations' && (
                  <TableReveal label="Top situations for doing this Job">
                    <SituationsTable rowsSource={tiles} customerStories={customerStories || []} jobName={selected} />
                  </TableReveal>
                )}

                {/* JOB view: Desired Outcomes table */}
                {mode === 'job' && kind === 'job.desiredOutcomes' && (
                  <TableReveal label="Desired outcomes for this Job">
                    <DesiredOutcomesTable rowsSource={tiles} customerStories={customerStories || []} jobName={selected} />
                  </TableReveal>
                )}

                {/* JOB view: Unified Insights table under "What 'good' & 'bad' look like" */}
                {mode === 'job' && kind === 'job.goodBad' && (
                  <TableReveal label="What ‘good’ & ‘bad’ look like (Insights)">
                    <UnifiedInsightsTable customerStories={customerStories || []} jobName={selected} />
                  </TableReveal>
                )}

                {/* JOB view: Products table under "Importance × Satisfaction" */}
                {mode === 'job' && kind === 'job.importanceSatisfaction' && (
                  <TableReveal label="How well products do this Job">
                    <ProductsTable
                      rowsSource={(tiles || []).filter(t => t.type === 'productForJob')}
                      customerStories={customerStories || []}
                      jobName={selected}
                    />
                  </TableReveal>
                )}

                {/* JOB view: Measures of Success table */}
                {mode === 'job' && kind === 'job.measures' && (
                  <TableReveal label="Measures of success">
                    <MeasuresTable rowsSource={tiles} customerStories={customerStories || []} jobName={selected} />
                  </TableReveal>
                )}
              </>
            )}
          </Box>
        );
      })}
      {/* Bottom-of-page interviews table */}
      {mode === 'job' && (customerStories?.length > 0) && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Interviews mentioning this Job" intro={null} />
          <TableReveal label="Interviews mentioning this Job">
            <JobInterviewsTable jobName={selected} customerStories={customerStories} variant="gap" />
          </TableReveal>
        </Box>
      )}
      {mode === 'product' && (customerStories?.length > 0) && (
        <Box sx={{ mb: 4 }}>
          <SectionHeader title="Interviews where this Product was used" intro={null} />
          <TableReveal label="Interviews where this Product was used">
            <ProductInterviewsTable productName={selected} customerStories={customerStories} />
          </TableReveal>
        </Box>
      )}
    </Box>
  );
}
